import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Box, Stack, Text } from '@chakra-ui/react';
import type { Bridge } from '@casa/bridge';
import { startDriverProbe, type DriverHostLink, type ResolvedDriver } from '@casa/driver-host';
import { isFormComplete, readForm, withFixedValues, type Form, type SourceDescription, type TestResult, type ValueInfo } from '@casa/driver-sdk';
import type { Dashboard } from '@casa/model';
import { FormView, type FormAsk } from '../forms/form_view.tsx';
import { SettingsGroup, SettingsRow } from '../settings/settings_ui.tsx';
import { Failed, Waiting, messageOf, useAsk, type Asked } from '../ui/asking.tsx';
import { ChromeError, ChromeNote } from '../ui/chrome_page.tsx';
import { createDriverWorker } from './drivers.ts';

// The driver explorer (dashboard-editor spec §6.1): one of the shown dashboard's connections, asked
// what its driver offers, step by step: each parameter of a data source in turn (the driver's list
// of choices, a fixed set, or a field), then what the source gives and the commands it takes, and
// a test of the connection. A driver of its own, with no sources, answers; it stops when the
// explorer closes. The editor's data selection (E6) uses the same form (`FormView`, spec §5.6),
// with a slot's needs, so that what fits the tile comes first (T-10).

const KIND_WORDS: Readonly<Record<ValueInfo['kind'], string>> = {
    number: 'a number',
    onOff: 'on and off',
    level: 'a level, 0 to 100',
    text: 'a text',
    history: 'a history (values over time)',
    record: 'a record of several values',
    forecast: 'a weather forecast',
    image: 'a still picture',
    video: 'a live video stream',
    light: 'a light: on or off, its level and colour',
    thermostat: 'a thermostat: the temperature, its set point and mode',
};

export function describeGives(gives: ValueInfo | undefined) : string {
    if (!gives) {
        return 'something the driver cannot describe yet';
    }

    return gives.unit ? `${KIND_WORDS[gives.kind]}, in ${gives.unit}` : KIND_WORDS[gives.kind];
}

/** What a source gives and the commands it takes; with its options as they are written (`showsOptions`) for the explorer. */
export function SourceSummary({ probe, options, showsOptions = true }: { probe: DriverHostLink; options: Record<string, unknown>; showsOptions?: boolean }) : ReactNode {
    const [asked, retry] = useAsk<SourceDescription>(JSON.stringify(options), () => probe.ask({ kind: 'describe', options }));

    let body: ReactNode;
    if (!asked || asked.status === 'waiting') {
        body = <Waiting text="Asking what it gives…" />;
    } else if (asked.status === 'failed') {
        body = <Failed reason={asked.reason} onRetry={retry} />;
    } else {
        const { gives, commands } = asked.value;
        body = (
            <>
                <SettingsRow icon="chart-line" title="Gives" detail={describeGives(gives)} />
                <SettingsRow
                    icon="hand-pointer"
                    title="Commands"
                    detail={
                        commands.length === 0
                            ? 'none'
                            : commands
                                .map((command) => {
                                    const argument = command.argument ? ` (${command.argument.kind}${command.argument.unit ? `, ${command.argument.unit}` : ''})` : '';

                                    return `${command.label ?? command.name}${argument}`;
                                })
                                .join(', ')
                    }
                />
            </>
        );
    }

    return (
        <Box data-summary>
            <SettingsGroup title="This data source" note={showsOptions ? <Text as="span" fontFamily="mono" fontSize="xs">{JSON.stringify(options)}</Text> : undefined}>
                {body}
            </SettingsGroup>
        </Box>
    );
}

function ConnectionTest({ probe }: { probe: DriverHostLink }) : ReactNode {
    const [result, setResult] = useState<Asked<TestResult>>();

    const run = async () : Promise<void> => {
        setResult({ status: 'waiting' });
        try {
            setResult({ status: 'done', value: await probe.ask({ kind: 'test' }) });
        } catch (error) {
            setResult({ status: 'failed', reason: messageOf(error) });
        }
    };

    return (
        <Stack gap="3">
            <SettingsGroup title="The connection">
                <SettingsRow icon="plug-circle-check" title="Test the connection" detail="Connect and read once" opens={false} onClick={() => void run()} />
            </SettingsGroup>
            {result?.status === 'waiting' ? <Waiting text="Testing…" /> : null}
            {result?.status === 'failed' ? <ChromeError text={result.reason} /> : null}
            {result?.status === 'done' ? (
                result.value.ok ? <ChromeNote status="success" text={result.value.detail} /> : <ChromeError text={`It did not work: ${result.value.reason}`} />
            ) : null}
        </Stack>
    );
}

export interface DriverExplorerProps {
    shell: Bridge;
    /** The connection's name in the dashboard. */
    name: string;
    config: Record<string, unknown>;
    driver: Extract<ResolvedDriver, { ok: true }>;
}

/** A driver of its own for a connection, to ask about its sources; stopped when the component goes. */
export function useDriverProbe(shell: Bridge, name: string, config: Record<string, unknown>, driver: Extract<ResolvedDriver, { ok: true }> | undefined) : DriverHostLink | undefined {
    const [probe, setProbe] = useState<DriverHostLink>();

    useEffect(() => {
        if (!driver) {
            setProbe(undefined);

            return undefined;
        }

        const started = startDriverProbe({
            bridge: shell,
            createWorker: createDriverWorker,
            connection: name,
            driver: driver.code,
            permissions: driver.permissions,
            config,
            log: (message) => console.warn(`probe: ${message}`),
        });
        setProbe(started);

        return () => started.dispose();
    }, [shell, name, config, driver]);

    return probe;
}

/** The form's questions, asked of a driver through its probe (§5.6): a field's choices with its path, a sub-form. */
export function formAskOf(probe: DriverHostLink, form: Form) : FormAsk {
    return {
        choices: (field, path, root) => probe.ask({ kind: 'choices', parameter: field.name, chosen: withFixedValues(form, root), path }),
        form: (path, root) => probe.ask({ kind: 'form', path, chosen: withFixedValues(form, root) }),
        describe: (root) => probe.ask({ kind: 'describe', options: withFixedValues(form, root) }),
    };
}

export function DriverExplorer({ shell, name, config, driver }: DriverExplorerProps) : ReactNode {
    const form = useMemo(() => readForm(driver.manifest.sourceSchema), [driver.manifest]);
    const [value, setValue] = useState<Record<string, unknown>>({});
    const probe = useDriverProbe(shell, name, config, driver);
    const ask = useMemo(() => (probe ? formAskOf(probe, form) : undefined), [probe, form]);

    if (!probe) {
        return null;
    }

    const isEmpty = form.fields.length === 0 && (form.alternatives ?? []).length === 0;

    return (
        <Stack gap="6">
            <ConnectionTest probe={probe} />
            {isEmpty ? (
                <SettingsGroup title="Its data sources" note="This driver does not describe its data sources.">
                    <SettingsRow title={<Text as="span" color="fg.muted">Nothing to choose</Text>} />
                </SettingsGroup>
            ) : (
                <SettingsGroup title="A data source">
                    <FormView form={form} value={value} onChange={setValue} ask={ask} />
                </SettingsGroup>
            )}
            {!isEmpty && isFormComplete(form, value) ? <SourceSummary probe={probe} options={withFixedValues(form, value)} /> : null}
        </Stack>
    );
}

/** The shown dashboard's connections, each opening its explorer. */
export function ConnectionsGroup({ dashboard, describeDriver, onOpen }: { dashboard: Dashboard | undefined; describeDriver: (driver: string) => string; onOpen: (name: string) => void }) : ReactNode {
    const names = Object.keys(dashboard?.connections ?? {});

    return (
        <SettingsGroup title="This dashboard's connections" note="Ask a connection's driver what it offers, step by step, and test the connection.">
            {names.length === 0 ? (
                <SettingsRow title={<Text as="span" color="fg.muted">{dashboard ? 'None' : 'No dashboard shown'}</Text>} />
            ) : (
                names.map((name) => <SettingsRow key={name} icon="plug" title={name} detail={describeDriver(dashboard?.connections[name]?.driver ?? '')} onClick={() => onOpen(name)} />)
            )}
        </SettingsGroup>
    );
}
